import { useCallback, useRef } from "react";
import { Row, Table } from "react-table-8.10.7";

export type ShiftRowSelectHandler<TData> = (args: {
  row: Row<TData>;
  table: Table<TData>;
  isShiftKey: boolean;
}) => void;

export const useShiftRowSelection = <TData>(): ShiftRowSelectHandler<TData> => {
  const anchorRowIdRef = useRef<string | null>(null);

  return useCallback(({ row, table, isShiftKey }) => {
    const rows = table.getRowModel().rows;
    const anchorIndex = rows.findIndex(
      ({ id }) => id === anchorRowIdRef.current
    );
    const rowIndex = rows.findIndex(({ id }) => id === row.id);
    const isAnchorSelected = rows[anchorIndex]?.getIsSelected() ?? false;
    const isSelecting = !row.getIsSelected();

    anchorRowIdRef.current = isSelecting ? row.id : null;

    if (!isShiftKey || !isAnchorSelected) {
      row.toggleSelected();
      return;
    }

    const rangeRowIds = new Set(
      rows
        .slice(
          Math.min(anchorIndex, rowIndex),
          Math.max(anchorIndex, rowIndex) + 1
        )
        .map(({ id }) => id)
    );

    table.setRowSelection((prev) => {
      const unchanged = Object.entries(prev).filter(
        ([id]) => !rangeRowIds.has(id)
      );
      const range = isSelecting ? [...rangeRowIds].map((id) => [id, true]) : [];

      return Object.fromEntries([...unchanged, ...range]);
    });
    window.getSelection()?.removeAllRanges();
  }, []);
};
